import React, { useEffect, useState } from 'react';

import Button from '@ingka/button';
import CrossIcon from '@ingka/ssr-icon/paths/cross';
import IncorrectIcon from '@ingka/ssr-icon/paths/incorrect';
import { ComponentIds, MetaDataComponent } from '@inter-ikea-core/core';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { ProductType } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';
import { getProductsInScene } from 'ducks/products';
import { addTV } from '../behaviors/addTV';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Box from 'ui/Box';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import KompisToolTip from 'ui/KompisToolTip/KompisToolTip';
import Title, { HeadingType } from 'ui/Typography/Title';
import media from 'ui/media';
import '@ingka/button/dist/style.css';
import '@ingka/focus/dist/style.css';
import '@ingka/modal/dist/style.css';
import '@ingka/svg-icon/dist/style.css';
import { reportTVStatus } from 'analytics/reporters';
import SlideOutMenu from 'components/SlideOutMenu/SlideOutMenu';
import { store } from 'ducks';
import {
  hideAddTVSheet,
  isAddTVSheetVisible,
  setTvSize as setTvSizeOption,
  setTvPlacement as setTvPlacementOption,
  TvPlacement,
  getTvPlacement,
  tvSizes,
  supportsStandingTv
} from '../TVFeature';
import { getRange } from 'utils/queryUtils';
import SegmentedControl from '@ingka/segmented-control';

const SelectedMenuWrapper = styled.div`
  height: 100%;
  padding: 0 ${({ theme }) => theme.space.xxl} 200px ${({ theme }) => theme.space.xxl};
  overflow-y: auto;
  ${media.mobile`
    padding: 0 1rem;
  `}
`;

const HeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 200;
  background: #fff;
  padding-top: ${({ theme }) => theme.space.md};
  padding-bottom: ${({ theme }) => theme.space.xs};
`;

const HeaderFlex = styled.div`
  display: flex;
  align-items: center;
  &[data-space-between='true'] {
    justify-content: space-between;
  }
`;

const ButtonWrapper = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  left: 0;
  right: 0;
  background-color: white;
  z-index: 10;
  border-top: 0.0625rem solid #dfdfdf;
  padding: 1.5rem 3rem 0.5rem 3rem;
  margin: 0;
`;

const StyledButton = styled(Button)`
  margin-bottom: 1rem;
`;

const SizeGrid = styled.div`
  margin-top: ${({ theme }) => theme.space.l};
  margin-bottom: ${({ theme }) => theme.space.xl};
  display: grid;
  border: 0;
  grid-template-columns: repeat(auto-fit, ${({ theme }) => theme.space.xxxxl});
  grid-template-rows: max-content;
  height: max-content;
  grid-gap: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xs};
  justify-content: left;
  width: 100%;
  padding: 0;
  ${media.mobile`
    margin: 1rem 0 2rem;
  `}
  &[data-selected='true'] {
    border: 2px solid black;
    padding: 7px;
    box-sizing: content-box;
  }
`;

const TvSizeRadioButtonLabel = styled.label`
  padding: 7px;
  box-sizing: content-box;
  cursor: pointer;

  &[data-selected='true'] {
    border-bottom: 2px solid black;
  }

  [type='radio'] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const TvSizeRadioButton = styled.input``;

function AddTVSheet() {
  const isVisible = !!useSelector(isAddTVSheetVisible);
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const coreApi = useCore();
  const rootState = store.getState();
  const tvPlacementOption = useSelector(getTvPlacement);
  const productsInScene = useSelector(getProductsInScene);
  const [tvSize, setTvSize] = useState<number | undefined>(55);
  const [tvPlacement, setTvPlacement] = useState<TvPlacement | undefined>(TvPlacement.wall);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [disableFootTV, setDisableFootTV] = useState<boolean>(true);
  const removeFootTvOption = getRange() === 'eket';

  const onAddTV = () => {
    addTV({ dispatch, coreApi, props: { tvSize, tvPlacement }, rootState });
    reportTVStatus({ enable: true });
    dispatch(setTvSizeOption(tvSize));
    dispatch(setTvPlacementOption(tvPlacement));
  };

  const onRemoveTV = () => {
    const tvIsInScene = coreApi.entities.getByProductTypes([
      ProductType.TV,
      ProductType.TV_WITH_FOOT
    ]);
    if (tvIsInScene.length > 0) {
      coreApi.entities.delete(tvIsInScene[0]);
    }
    reportTVStatus({ enable: false });
    onCloseSheet();
  };

  const onCloseSheet = () => {
    dispatch(hideAddTVSheet());
  };

  const onChangeTvSize = (size: number) => {
    setTvSize(size);
  };

  useEffect(() => {
    if (activeIndex === 0) {
      setTvPlacement(TvPlacement.wall);
    } else {
      setTvPlacement(TvPlacement.foot);
    }
  }, [activeIndex]);

  useEffect(() => {
    const anyBenches = !!coreApi.entities.getByProductTypes(supportsStandingTv).length;

    if (anyBenches) {
      setDisableFootTV(false);

      if (!tvPlacementOption) {
        setTvPlacement(TvPlacement.foot);
        setActiveIndex(1);
      }
    } else {
      setDisableFootTV(true);
      setTvPlacement(TvPlacement.wall);
      setActiveIndex(0);
    }
  }, [coreApi.entities, isVisible, tvPlacementOption, productsInScene]);

  useEffect(() => {
    const updateTvOptions = () => {
      const tvIsInScene = coreApi.entities.getByProductTypes([
        ProductType.TV,
        ProductType.TV_WITH_FOOT
      ]);
      if (tvIsInScene.length > 0) {
        const tvSizeInScene = tvIsInScene[0].getComponent<MetaDataComponent>(
          ComponentIds.META_DATA_COMPONENT_ID
        )?.metaData.tvSize;
        if (tvSizeInScene) {
          dispatch(setTvSizeOption(tvSizeInScene as number));
          setTvSize(tvSizeInScene as number);
        }
        if (coreApi.entities.isProductType(tvIsInScene[0], ProductType.TV)) {
          setTvPlacement(TvPlacement.wall);
          setActiveIndex(0);
        } else {
          setTvPlacement(TvPlacement.foot);
          setActiveIndex(1);
        }
      }
    };

    return coreApi.subscribe('load', updateTvOptions);
  }, [coreApi, dispatch, tvSize, tvPlacement]);

  return (
    <SlideOutMenu trigger={isVisible} data-testid="add-tv-modal">
      <SelectedMenuWrapper>
        <HeaderWrapper>
          <HeaderFlex data-space-between>
            <Title type={ThemeFontStyleTypeEnum.headingMedium}>
              {formatMessage({ id: 'storageone.addTvSheetTitle' })}
            </Title>
            <Button
              data-testid="add-tv-close-button"
              onClick={onCloseSheet}
              ssrIcon={CrossIcon}
              aria-label={formatMessage({ id: 'storageone.closeButton' })}
              type="tertiary"
              iconOnly
            />
          </HeaderFlex>
        </HeaderWrapper>
        <Title type={ThemeFontStyleTypeEnum.headingSmall} heading={HeadingType.HeadingTwo}>
          {formatMessage({ id: 'storageone.addTvSheetHeaderSize' })}
        </Title>
        <SizeGrid role="group">
          {tvSizes.map(size => (
            <KompisToolTip text={`${size}"`} uniqueId={`${size}`} key={size}>
              <TvSizeRadioButtonLabel key="size-radio" data-selected={size === tvSize}>
                <TvSizeRadioButton
                  data-testid={`add-tvsize-${size}`}
                  data-selected={size === tvSize}
                  type="radio"
                  name="size-radio"
                  value={size}
                  checked={tvSize ? size === tvSize : size === 55}
                  onChange={() => onChangeTvSize(size)}
                />
                {size}”
              </TvSizeRadioButtonLabel>
            </KompisToolTip>
          ))}
        </SizeGrid>

        {!removeFootTvOption && (
          <>
            <Title
              mb="md"
              type={ThemeFontStyleTypeEnum.headingSmall}
              heading={HeadingType.HeadingTwo}
            >
              {formatMessage({ id: 'storageone.addTvSheetHeaderPlacement' })}
            </Title>
            <SegmentedControl
              data-testid="add-tv-type-toggle"
              activeIndex={activeIndex}
              items={[
                {
                  label: formatMessage({ id: 'storageone.addTvSheetChoiceWall' })
                },
                {
                  label: formatMessage({ id: 'storageone.addTvSheetChoiceStanding' }),
                  disabled: disableFootTV
                }
              ]}
              onClick={(eventObj, index) => setActiveIndex(index)}
            />

            {disableFootTV && (
              <Box mt="l">
                <SkapaInlineMessage
                  body={formatMessage({ id: 'storageone.addTvSheetErrorMsg' })}
                  variant={InlineMessageVariantEnum.NEGATIVE}
                  ssrIcon={IncorrectIcon}
                  subtle
                />
              </Box>
            )}
          </>
        )}
        <ButtonWrapper>
          <StyledButton
            text={formatMessage({ id: 'storageone.addTvSheetPrimaryButton' })}
            onClick={onAddTV}
            type="primary"
            data-testid="add-tv"
            fluid
          />
          <StyledButton
            text={formatMessage({ id: 'storageone.addTvSheetSecondaryButton' })}
            onClick={onRemoveTV}
            type="secondary"
            data-testid="remove-tv"
            fluid
          />
        </ButtonWrapper>
      </SelectedMenuWrapper>
    </SlideOutMenu>
  );
}

export default AddTVSheet;
